import { abilityEffectId } from "./ability-effects.ts";
import { ATTRIBUTES, isAttribute, type Attribute } from "./attributes.ts";
import { fieldsOf, flag, iconOf, identifier, isPlainRecord, name, oneOf, report, ticks, whole, type Place } from "./checks.ts";
import { CONTROL_KINDS, DAMAGE_TYPES, DEATH_CAUSES, MAX_EFFECT_TICKS, STACKING_RULES, type Ability, type ControlKind, type DeathCause, type Effect, type EffectModifiers, type Periodic } from "./types.ts";

/**
 * The effects table's record check (ADR-079 decision 1): an effect's name and duration, whether watchers see it, its
 * control category, its stacking, its death rules, its five modifiers and its optional ones, each held to its bounds,
 * the damage it deals over time (its amendment of 2026-10-02) and its icon's key (ADR-111 decision 1). The validator
 * runs it for each record and checks across the table that no death cause has two penalties.
 */

const EFFECT_FIELDS = ["id", "name", "durationTicks", "visible", "control", "stacking", "maxStacks", "persistsDeath", "onDeath", "modifiers", "periodic", "icon"] as const;
const PERIODIC_FIELDS = ["everyTicks", "amount", "type", "cause"] as const;
const MAX_PERIODIC_AMOUNT = 255;
const MODIFIER_FIELDS = ["damageDealt", "damageTaken", "maxHealth", "cooldown", "absorb"] as const;
const OPTIONAL_MODIFIER_FIELDS = ["focusRegeneration", "moveSpeed"] as const;
const MAX_ATTRIBUTE_POINTS = 10;
const MAX_STACKS = 255;
const MAX_ABSORB = 65_535;

/**
 * Each modifier's bounds in whole percents, or points for `absorb`: damage dealt may fall to nothing, damage taken only
 * falls (an increase waits for a vulnerability rule), a maximum keeps at least a point, and a cooldown only lengthens,
 * since a cooldown never runs shorter than its swing (ADR-073's amendment, item 1). Focus regeneration may stop or grow
 * tenfold (ADR-137's amendment of 2026-10-28). Move speed only slows, by at most half (ADR-144's amendment of
 * 2026-11-01, Drenched).
 */
const MODIFIER_BOUNDS: Readonly<Record<Exclude<keyof EffectModifiers, "attributes">, readonly [number, number]>> = {
  damageDealt: [-100, 1000],
  damageTaken: [-100, 0],
  maxHealth: [-99, 1000],
  cooldown: [0, 1000],
  absorb: [0, MAX_ABSORB],
  focusRegeneration: [-100, 1000],
  moveSpeed: [-50, 0],
};

/**
 * Checks a nullable member of a closed set.
 *
 * @param place Where to report.
 * @param value The value, null for none.
 * @param path Its path.
 * @param set The closed set.
 * @param what What a member is called.
 * @returns The member, null for none, or undefined when it is missing or wrong.
 */
function nullableOf<T extends string>(place: Place, value: unknown, path: string, set: readonly T[], what: string): T | null | undefined {
  if (value === null) {
    return null;
  }
  return oneOf(place, value, path, set, what) ?? undefined;
}

/**
 * Checks an effect's attribute modifier (ADR-138 decision 7): one to seven of the attributes, each a whole number of
 * flat points a stack from -10 to 10 and never 0.
 *
 * @param place Where to report.
 * @param value The value.
 * @returns The map, or null.
 */
function attributeModifiersOf(place: Place, value: unknown): Readonly<Partial<Record<Attribute, number>>> | null {
  if (!isPlainRecord(value) || Object.keys(value).length === 0) {
    report(place, `modifiers.attributes must be an object of one or more attributes (${ATTRIBUTES.join(", ")}) to points`);
    return null;
  }
  const read: Partial<Record<Attribute, number>> = {};
  let ok = true;
  for (const attribute of Object.keys(value)) {
    if (!isAttribute(attribute)) {
      report(place, `modifiers.attributes.${attribute} is not an attribute (${ATTRIBUTES.join(", ")})`);
      ok = false;
      continue;
    }
    const points = whole(place, value[attribute], `modifiers.attributes.${attribute}`, -MAX_ATTRIBUTE_POINTS, MAX_ATTRIBUTE_POINTS);
    if (points === null) {
      ok = false;
    } else if (points === 0) {
      report(place, `modifiers.attributes.${attribute} is 0; leave it out instead`);
      ok = false;
    } else {
      read[attribute] = points;
    }
  }
  return ok ? read : null;
}

/**
 * Checks an effect's modifiers: exactly the five, each a whole number within its bounds, any of the optional ones, each
 * a whole number within its bounds and not 0, so a record that changes nothing by one leaves it out, and the optional
 * attribute modifier.
 *
 * @param place Where to report.
 * @param value The modifiers.
 * @returns The modifiers, or null.
 */
function modifiersOf(place: Place, value: unknown): EffectModifiers | null {
  const optional = isPlainRecord(value) ? OPTIONAL_MODIFIER_FIELDS.filter((field) => Object.hasOwn(value, field)) : [];
  const hasAttributes = isPlainRecord(value) && Object.hasOwn(value, "attributes");
  const fields = fieldsOf(place, value, "modifiers", [...MODIFIER_FIELDS, ...optional, ...(hasAttributes ? ["attributes"] : [])], "the modifiers");
  if (fields === null) {
    return null;
  }
  const read: Partial<Record<keyof EffectModifiers, unknown>> = {};
  let ok = Object.keys(fields).length === MODIFIER_FIELDS.length + optional.length + (hasAttributes ? 1 : 0);
  for (const field of [...MODIFIER_FIELDS, ...optional]) {
    const [min, max] = MODIFIER_BOUNDS[field];
    const checked = whole(place, fields[field], `modifiers.${field}`, min, max);
    if (checked === null) {
      ok = false;
    } else if (checked === 0 && (OPTIONAL_MODIFIER_FIELDS as readonly string[]).includes(field)) {
      report(place, `modifiers.${field} is 0; leave it out instead`);
      ok = false;
    } else {
      read[field] = checked;
    }
  }
  if (hasAttributes) {
    const attributes = attributeModifiersOf(place, fields["attributes"]);
    if (attributes === null) {
      ok = false;
    } else {
      read.attributes = attributes;
    }
  }
  return ok ? (read as unknown as EffectModifiers) : null;
}

/**
 * Checks what an effect deals over time: null for nothing, or its interval in ticks, its damage a stack (1 to 255), its
 * damage type and the name a death line gives it.
 *
 * @param place Where to report.
 * @param value The periodic record.
 * @returns The record, null for none, or undefined when it is missing or wrong.
 */
function periodicOf(place: Place, value: unknown): Periodic | null | undefined {
  if (value === null) {
    return null;
  }
  const fields = fieldsOf(place, value, "periodic", PERIODIC_FIELDS, "a periodic effect");
  if (fields === null) {
    return undefined;
  }
  const everyTicks = ticks(place, fields["everyTicks"], "periodic.everyTicks", 1, MAX_EFFECT_TICKS);
  const amount = whole(place, fields["amount"], "periodic.amount", 1, MAX_PERIODIC_AMOUNT);
  const type = oneOf(place, fields["type"], "periodic.type", DAMAGE_TYPES, "a damage type");
  const cause = name(place, fields["cause"], "periodic.cause");
  if (everyTicks === null || amount === null || type === null || cause === null || Object.keys(fields).length !== PERIODIC_FIELDS.length) {
    return undefined;
  }
  return { everyTicks, amount, type, cause };
}

/**
 * Checks a periodic effect's duration: a whole number of its interval, so each application ticks a whole number of
 * times and its last tick falls in the tick it ends.
 *
 * @param place Where to report.
 * @param durationTicks The duration, or null when it was wrong.
 * @param periodic What it deals over time, null for nothing, or undefined when it was wrong.
 * @returns Whether they agree.
 */
function durationAgrees(place: Place, durationTicks: number | null, periodic: Periodic | null | undefined): boolean {
  if (durationTicks !== null && periodic !== null && periodic !== undefined && durationTicks % periodic.everyTicks !== 0) {
    report(place, `durationTicks is ${durationTicks}, and a periodic effect lasts a whole number of its ${periodic.everyTicks} ticks`);
    return false;
  }
  return true;
}

/**
 * Checks an effect's stacking against its stacks and control: a refreshing effect holds one stack, and a controlling
 * effect refreshes, so its diminishing returns count applications, never stacks or intensity.
 *
 * @param place Where to report.
 * @param stacking The stacking rule, or null when it was wrong.
 * @param maxStacks The most stacks, or null when it was wrong.
 * @param control The control category, null for none, or undefined when it was wrong.
 * @returns Whether they agree.
 */
function stackingAgrees(place: Place, stacking: string | null, maxStacks: number | null, control: ControlKind | null | undefined): boolean {
  if (stacking === "refresh" && maxStacks !== null && maxStacks !== 1) {
    report(place, `maxStacks is ${maxStacks}, and a refreshing effect holds 1`);
    return false;
  }
  if (stacking !== "refresh" && stacking !== null && control !== null && control !== undefined) {
    report(place, "a controlling effect refreshes; it never stacks");
    return false;
  }
  return true;
}

/**
 * Checks an effect. Its optional `tip` names a line of the language files' `bar.effectTips` its tooltip adds (an
 * identifier), for what no other field says, as Drenched's "dries out of the rain".
 *
 * @param place Where to report.
 * @param record The record.
 * @returns The effect, or null.
 */
export function effectOf(place: Place, record: Readonly<Record<string, unknown>>): Effect | null {
  const { tip: rawTip, ...rest } = record;
  const fields = fieldsOf(place, rest, "", EFFECT_FIELDS, "an effect") as Readonly<Record<string, unknown>>;
  const tip = rawTip === undefined ? undefined : identifier(place, rawTip, "tip");
  const id = identifier(place, fields["id"], "id");
  const effectName = name(place, fields["name"], "name");
  const durationTicks = ticks(place, fields["durationTicks"], "durationTicks", 1, MAX_EFFECT_TICKS);
  const visible = flag(place, fields["visible"], "visible");
  const control = nullableOf(place, fields["control"], "control", CONTROL_KINDS, "a control category");
  const stacking = oneOf(place, fields["stacking"], "stacking", STACKING_RULES, "a stacking rule");
  const maxStacks = whole(place, fields["maxStacks"], "maxStacks", 1, MAX_STACKS);
  const persistsDeath = flag(place, fields["persistsDeath"], "persistsDeath");
  const onDeath: DeathCause | null | undefined = nullableOf(place, fields["onDeath"], "onDeath", DEATH_CAUSES, "a death cause");
  const modifiers = modifiersOf(place, fields["modifiers"]);
  const periodic = fields["periodic"] === undefined ? undefined : periodicOf(place, fields["periodic"]);
  const icon = iconOf(place, fields["icon"]);
  const agrees = stackingAgrees(place, stacking, maxStacks, control) && durationAgrees(place, durationTicks, periodic);
  if (id === null || effectName === null || durationTicks === null || visible === null || control === undefined || stacking === null || maxStacks === null || persistsDeath === null || onDeath === undefined || modifiers === null || periodic === undefined || icon === undefined || tip === null || !agrees || Object.keys(fields).length !== EFFECT_FIELDS.length) {
    return null;
  }
  return { id, name: effectName, durationTicks, visible, control, stacking, maxStacks, persistsDeath, onDeath, modifiers, periodic, icon, ...(tip === undefined ? {} : { tip }) };
}

/**
 * Reports every effect that is a second penalty for a death cause another effect already is.
 *
 * @param effects The effects that passed their checks, with the names their problems carry, in the table's order.
 * @param problems Where problems go.
 */
export function checkPenalties(effects: readonly { readonly name: string; readonly record: Effect }[], problems: Place["problems"]): void {
  const held = new Map<DeathCause, string>();
  for (const { name: effect, record } of effects) {
    if (record.onDeath === null) {
      continue;
    }
    const first = held.get(record.onDeath);
    if (first === undefined) {
      held.set(record.onDeath, effect);
    } else {
      report({ table: "effects", id: effect, problems }, `a second penalty for a ${record.onDeath} death; ${first} is one already`);
    }
  }
}

/**
 * Checks the diminishing returns' window against the cooldowns (ADR-079's amendment of 2026-10-07): `diminishing_window`
 * must be shorter than the shortest cooldown of an ability that applies a control effect, on landing or on a hit, so one
 * attacker's stun used on cooldown is never shortened. A window its own record refused is not checked again.
 *
 * @param window The checked `diminishing_window`, or null when the constants hold none that passed.
 * @param abilities The abilities that passed their checks, with the names their problems carry.
 * @param effects The effects that passed their checks.
 * @param problems Where problems go.
 */
export function checkDiminishingWindow(
  window: number | null,
  abilities: readonly { readonly name: string; readonly record: Ability }[],
  effects: readonly { readonly record: Effect }[],
  problems: Place["problems"],
): void {
  if (window === null) {
    return;
  }
  const controlling = new Set(effects.filter(({ record }) => record.control !== null).map(({ record }) => record.id));
  let least: { readonly name: string; readonly ticks: number } | null = null;
  for (const { name, record } of abilities) {
    const applies = record.effects.some((entry) => controlling.has(abilityEffectId(entry))) || record.onHit.some((entry) => controlling.has(entry.effect));
    if (applies && (least === null || record.cooldownTicks < least.ticks)) {
      least = { name, ticks: record.cooldownTicks };
    }
  }
  if (least !== null && window >= least.ticks) {
    report(
      { table: "constants", id: "diminishing_window", problems },
      `ticks is ${window}, and it must be shorter than ${least.ticks}, the cooldown of ${least.name}, the least of an ability that applies a control effect: one attacker's stun on cooldown would be shortened`,
    );
  }
}
